<template>

    <div class="flex">
        <div class="rounded border border-primary-20 overflow-hidden shadow-md">

            <div class="h-320 flex flex-col">

                <div class="flex-none pt-8 px-8 bg-primary-5 border-b-2 border-primary-20">
                    <div class="mb-8 relative">
                        <label
                            for="emoji-search"
                            class="cursor-pointer absolute inset-y-0 left-0 h-full flex items-center text-primary-80 pl-12"
                        >
                            <svg-icon type="search" class="max-w-16 max-h-16"></svg-icon>
                        </label>
                        <input
                            ref="emojiSearch"
                            id="emoji-search"
                            class="border border-primary-20 outline-none focus:ring-2 ring-secondary-20 rounded-32 h-32 pl-40 pr-12 placeholder-primary-40 w-full"
                            type="text"
                            v-model="search"
                            autocomplete="off"
                        >
                    </div>

                    <ul class="flex justify-around pt-8 mb-8">
                        <li
                            v-for="(icon, category) in categoryIcons"
                            :key="category"
                            class=""
                        >
                            <button
                                :disabled="category === activeCategory"
                                class="text-primary-60 disabled:text-secondary hover:text-primary trans-ease"
                                @click="scrollTo(category)"
                            >
                                <svg-icon :type="icon" class="max-w-24 max-h-24"></svg-icon>
                            </button>
                        </li>
                    </ul>

                </div>

                <div
                    class="bg-white shrink overflow-y-scroll px-8"
                    @scroll="handleScroll"
                    ref="scrollContainer"
                >
                    <div class="mb-8 relative" style="width: 28.8rem">
                        <div
                            v-for="(emojis, category) in emojiData"
                            :key="category"
                            :ref="category + 'Container'"
                        >
                            <div class="sticky top-0 pt-8 capitalize font-semibold pl-8 text-body overflow-auto">
                                <div class="h-full w-full bg-white opacity-85 z-10 absolute inset-0"></div>
                                <div class="mb-4 relative z-20">
                                    {{ $t('general.emoji_groups.' + _snakeCase(category)) }}
                                </div>
                            </div>
                            <div class="font-auto flex flex-wrap mb-4" style="font-size: 26px;">
                                <div
                                    v-for="emoji in emojis"
                                    :key="emoji.order"
                                    class="flex-none"
                                    style="width: 3.6rem; height: 3.6rem;"
                                >
                                    <button
                                        class="rounded-sm w-full h-full flex items-center justify-center hover:bg-primary-20 trans-ease"
                                        @mouseover="previewEmoji(emoji)"
                                        @mouseleave="resetPreview"
                                        @click="pickEmoji(emoji)"
                                    >
                                        <span class="flex-none pt-px">{{ emoji.skins[skinTone] }}</span>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

            </div>

            <div class="bg-primary-5 overflow-auto border-t border-primary-20 pt-24 pl-16">

                <div class="mb-12 flex items-center" style="width: 28.8rem">
                    <div class="font-auto text-big leading-none mr-12">
                        {{ preview.emoji }}
                    </div>
                    <div class="-mt-8 shrink text-body italic leading-tight text-primary-80 first-letter:uppercase">
                        {{ preview.annotation }}
                    </div>
                </div>

            </div>

        </div>
    </div>

</template>

<script>
import emojis from '../../etc/emojis';
import _snakeCase from 'lodash/snakeCase';
import SvgIcon from './SvgIcon.vue';
import _throttle from 'lodash/throttle';
import { mapState } from 'pinia';
import {useMainStore} from '../../state/store';

export default {
    components: {
        'svg-icon': SvgIcon,
    },
    data: function () {
        return {
            skinTone: 'default',
            search: '',
            categoryIcons: {
                smileysAndPeople: 'mood',
                animalsAndNature: 'emoji-nature',
                foodAndDrink: 'emoji-food-beverage',
                travelAndPlaces: 'travel',
                activities: 'sports-soccer',
                objects: 'emoji-objects',
                symbols: 'stars',
                flags: 'flag',
            },
            activeCategory: 'mood',
            previewing: null,
        }
    },
    computed: {
        ...mapState(useMainStore, ['locale']),
        preview () {

            if (this.previewing) {
                return {
                    emoji: this.previewing.skins[this.skinTone],
                    shortcode: this.previewing.shortcodes[0],
                    annotation: this.previewing.annotation[this.locale],
                }
            } else {

                let emoji = emojis.find(emoji => emoji.shortcodes.includes('point_up'));

                return {
                    emoji: emoji.skins[this.skinTone],
                    annotation: this.$t('general.pick_emoji'),
                }
            }
        },
        emojiData () {

            let search = this.search.trim().toLowerCase();

            let filteredEmojis = search.length >= 2 ? emojis.filter(emoji => {
                return emoji.tags.join('|').toLowerCase().includes(search) || Object.values(emoji.annotation).join('|').toLowerCase().includes(search);
            }) : emojis;

            return {
                smileysAndPeople: filteredEmojis
                .filter(emoji => ['smileys-emotion', 'people-body'].includes(emoji.group))
                .sort((a,b) => a -b),
                animalsAndNature: filteredEmojis
                .filter(emoji => emoji.group === 'animals-nature')
                .sort((a,b) => a -b),
                foodAndDrink: filteredEmojis
                .filter(emoji => emoji.group === 'food-drink')
                .sort((a,b) => a -b),
                travelAndPlaces: filteredEmojis
                .filter(emoji => emoji.group === 'travel-places')
                .sort((a,b) => a -b),
                activities: filteredEmojis
                .filter(emoji => emoji.group === 'activities')
                .sort((a,b) => a -b),
                objects: filteredEmojis
                .filter(emoji => emoji.group === 'objects')
                .sort((a,b) => a -b),
                symbols: filteredEmojis
                .filter(emoji => emoji.group === 'symbols')
                .sort((a,b) => a -b),
                flags: filteredEmojis
                .filter(emoji => emoji.group === 'flags')
                .sort((a,b) => a -b),
            }
        },
        categoryDimensions () {
            let dimensions = {};

            Object.keys(this.emojiData).forEach(category => {

                let el = this.$refs[category + 'Container'][0];

                dimensions[category] = {
                    start: el.offsetTop,
                    end: el.offsetTop + el.offsetHeight,
                }
            });

            return dimensions;
        }
    },
    methods: {
        _snakeCase,
        handleScroll: _throttle(function (e) {
            let scrollTop = this.$refs.scrollContainer.scrollTop;

            let hit = Object.values(this.categoryDimensions).findIndex(category => {
                return category.start <= scrollTop && category.end > scrollTop;
            });

            this.activeCategory = Object.keys(this.categoryDimensions)[hit];
        }, 50),
        scrollTo (category) {
            this.$refs.scrollContainer.scrollTop = this.categoryDimensions[category].start;
            this.$refs.emojiSearch.focus();
        },
        previewEmoji (emoji) {
            this.previewing = emoji;
        },
        resetPreview () {
            this.previewing = null;
        },
        pickEmoji (emoji) {
            this.$emit('select', emoji.skins[this.skinTone]);
        },
        focus () {
            this.$refs.emojiSearch.focus();
        }
    }
}
</script>

<style scoped>
.fade-enter-active, .fade-leave-active {
  transition: opacity .2s;
}
.fade-enter, .fade-leave-to /* .fade-leave-active below version 2.1.8 */ {
  opacity: 0;
}
</style>
